/**
 * Formatting helpers for culture-aware number and date display.
 *
 * - `formatNumber` formats numeric values with fixed decimal places.
 * - Date helpers derive culture-specific placeholder/format strings and convert
 *   `DD/MM/YYYY` input into the correct delimiter/order for the current
 *   culture.
 */
export const formatNumber = (
  val: number | undefined,
  dp: number,
  culture: string
): string => {
  const value = val === undefined ? 0 : val;
  const formattedValue = new Intl.NumberFormat(culture, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  }).format(value);
  return formattedValue;
};

export const getDateFormatString = (culture: string): string => {
  const formatObj = new Intl.DateTimeFormat(culture).formatToParts(new Date());
  return formatObj
    .map((obj) => {
      switch (obj.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'MM';
        case 'year':
          return 'yyyy';
        case 'literal':
          return obj.value;
        default:
          return obj.value;
      }
    })
    .join('');
};

export const getPlaceHolderFormatString = (culture: string): string => {
  const formatObj = new Intl.DateTimeFormat(culture).formatToParts(new Date());
  return formatObj
    .map((obj) => {
      switch (obj.type) {
        case 'day':
          return 'DD';
        case 'month':
          return 'MM';
        case 'year':
          return 'YYYY';
        case 'literal':
          return obj.value;
        default:
          return obj.value;
      }
    })
    .join('');
};

export const getDayMonthOrder = (culture: string): boolean => {
  const formatObj = new Intl.DateTimeFormat(culture).formatToParts(new Date());

  const delimiter = formatObj?.find((f) => f.type === 'literal')?.value ?? '/';

  return formatObj
    .map((obj) => {
      switch (obj.type) {
        case 'day':
          return 'DD';
        case 'month':
          return 'MM';
        case 'year':
          return 'YYYY';
        case 'literal':
          return obj.value;
        default:
          return obj.value;
      }
    })
    .join('')
    .split(delimiter)[0] === 'DD'
    ? true
    : false;
};

export const getDateStringByBrowserLanguage = (
  culture: string,
  inputDate: string
): string => {
  if (inputDate === '' || inputDate === null || inputDate === undefined) {
    return '';
  } else {
    const isDayFirst = getDayMonthOrder(culture);

    const formatObj = new Intl.DateTimeFormat(culture).formatToParts(
      new Date()
    );
    const delimiter =
      formatObj?.find((f) => f.type === 'literal')?.value ?? '/';

    const [day, month, year] = inputDate.split('/');
    if (isDayFirst) {
      return `${day}${delimiter}${month}${delimiter}${year}`;
    } else {
      return `${month}${delimiter}${day}${delimiter}${year}`;
    }
  }
};

export const getDateByBrowserLanguage = (
  culture: string,
  inputDate: string
): Date | null => {
  if (!inputDate) {
    return null;
  } else {
    const formatObj = new Intl.DateTimeFormat(culture).formatToParts(
      new Date()
    );

    const delimiter =
      formatObj?.find((f) => f.type === 'literal')?.value ?? '/';

    const [partA, partB, year] = inputDate.split(delimiter);

    const isDayFirst = getDayMonthOrder(culture);

    if (isDayFirst) {
      return new Date(Number(year), Number(partB) - 1, Number(partA));
    } else {
      return new Date(Number(year), Number(partA) - 1, Number(partB));
    }
  }
};
